/**
 * [LEAD-ERROR-LOG 2026-10-30, user] "make sure the app error thing should work fine. So that every time something gets happened, like the error or something in the leads
 * management tab, then I should get the app error for that. So that I will share it with you then on this chat". What only the browser can see on Lead Management goes to
 * the server (POST /api/lead-assignment/client-error → App Alerts' lead_errors card):
 *   crash   the page crashed (ErrorBoundary)
 *   script  a script error outside a render (a click's code, an async step)
 *   api     a request to /lead-assignment that got no answer from OUR server: no connection, a time-out, the proxy's 502 / 503 / 504
 * NOT reported: a refusal the page explains ("already marked": a 4xx), a request someone cancelled, and our server's own 5xx (it has already recorded that itself).
 * Every such failure counts at once (a page load has already had api.ts's two quiet retries by then), except the 2-second poll of a running step: that one from its 2nd failure in
 * a row (its next poll usually works, and nobody sees the first).
 * Uses fetch, not `api`, so a failing report can never report itself. Nothing shows on screen and nothing here can break the page.
 */
const REPORT_URL = '/api/lead-assignment/client-error';
const REPEAT_MS = 60_000; // the same error again within a minute is not sent again (the server's table counts the ones it gets)
const MAX_PER_PAGE_LOAD = 30;
const sent = new Map<string, number>();
let total = 0;

export interface LeadClientError {
  kind: 'crash' | 'script' | 'api';
  message: string;
  where?: string;
  status?: number | null;
  stack?: string | null;
}

/** True on the Lead Management tab. */
export const onLeadPage = (): boolean => { try { return window.location.pathname.startsWith('/lead-assignment'); } catch { return false; } };

export function reportLeadClientError(e: LeadClientError): void {
  try {
    const key = `${e.kind}|${e.where ?? ''}|${e.message}`.slice(0, 400);
    const now = Date.now();
    if (now - (sent.get(key) ?? 0) < REPEAT_MS || total >= MAX_PER_PAGE_LOAD) return;
    sent.set(key, now);
    total++;
    void fetch(REPORT_URL, {
      method: 'POST', credentials: 'include', keepalive: true, headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ kind: e.kind, message: String(e.message).slice(0, 1000), where: e.where, status: e.status ?? null, stack: e.stack ? String(e.stack).slice(0, 8000) : null, page: `${window.location.pathname}${window.location.search}`.slice(0, 255) }),
    }).catch(() => undefined); // no connection: nothing can be sent, and the person's own connection is no fault of the app
  } catch { /* nothing here may break the page */ }
}

/* ── requests (services/api.ts calls these two) ── */
const LEAD_URL = /^\/?lead-assignment(?:[/?]|$)/;
const failing = new Map<string, number>();
/** "GET /lead-assignment/:id/email" — the action, whichever row it was on */
const actionOf = (config: any): string | null => {
  const url = String(config?.url ?? '');
  if (!LEAD_URL.test(url)) return null;
  return `${String(config?.method ?? 'get').toUpperCase()} /${url.replace(/^\//, '').split('?')[0].replace(/\/\d+(?=\/|$)/g, '/:id')}`;
};

export function noteLeadApiOk(config: any): void {
  const a = actionOf(config);
  if (a && failing.size) failing.delete(a);
}

export function noteLeadApiFailure(error: any): void {
  try {
    const a = actionOf(error?.config);
    if (!a) return;
    if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') return;
    const res = error?.response;
    const status: number | null = res?.status ?? null;
    if (status != null && status < 500) return; // a refusal the page explains
    if (res?.data && typeof res.data === 'object' && 'error' in res.data) return; // our server answered: it has recorded its own failure
    const n = (failing.get(a) ?? 0) + 1;
    failing.set(a, n);
    if (a === 'GET /lead-assignment/job' && n < 2) return;
    const message = status != null ? `The page got HTTP ${status} from the proxy, not from our server`
      : error?.code === 'ECONNABORTED' ? `The server did not answer in time (${String(error?.message || '').slice(0, 120)})`
        : `The request got no answer (${String(error?.message || 'network error').slice(0, 120)})`;
    reportLeadClientError({ kind: 'api', where: a, message, status });
  } catch { /* nothing here may break the page */ }
}

/* ── script errors outside a render ── */
const NOISE = /ResizeObserver loop|^Script error\.?$/i;
let listening = false;
/** Once, at the app's start: reports only while Lead Management is on screen. */
export function installLeadErrorListeners(): void {
  if (listening) return;
  listening = true;
  window.addEventListener('error', (ev: ErrorEvent) => {
    try {
      const message = String(ev.message || ev.error?.message || '');
      if (!onLeadPage() || !message || NOISE.test(message) || /^(chrome|moz)-extension:/.test(String(ev.filename || ''))) return;
      reportLeadClientError({ kind: 'script', message, stack: ev.error?.stack ?? `${ev.filename}:${ev.lineno}:${ev.colno}` });
    } catch { /* nothing here may break the page */ }
  });
  window.addEventListener('unhandledrejection', (ev: PromiseRejectionEvent) => {
    try {
      const r: any = ev.reason;
      if (!onLeadPage() || r?.isAxiosError || r?.name === 'CanceledError' || r?.name === 'AbortError') return; // a request's failure is judged above
      const message = String(r?.message ?? r ?? '');
      if (message && !NOISE.test(message)) reportLeadClientError({ kind: 'script', message, stack: r?.stack ?? null });
    } catch { /* nothing here may break the page */ }
  });
}
